import { createContext, useCallback, useContext, useState } from 'react';
import DelegateModal, { DelegateModalProps } from 'components/modals/delegate/DelegateModal';

/**
 * Parameters for the delegate function.
 */
export type IDelegateParams = Omit<DelegateModalProps, 'opened' | 'onClose'>;

/**
 * Delegate function type.
 */
export type IDelegate = (params: IDelegateParams) => void;

/**
 * Context interface for DelegateProvider.
 */
export interface IDelegateContext {
  delegate: IDelegate;
}

/**
 * The delegate context.
 */
const DelegateContext = createContext<IDelegateContext>(undefined!);

/**
 * Provides the DelegateContext.
 */
export default function DelegateProvider({ children }: { children: React.ReactNode }) {
  const [opened, setOpened] = useState(false);

  const [params, setParams] = useState<IDelegateParams>(() => ({
    revisionKind: 'revision',
    title: '',
    content: '',
    revisionId: 0,
    departmentId: 0,
    deviceTypeId: 0,
    technicianId: 0,
    delegatedUsers: [],
    onDelegate: () => {},
  }));

  /**
   * Delegate function.
   */
  const delegate: IDelegate = useCallback(
    (params) => {
      setParams(params);
      setOpened(true);
    },
    [setParams, setOpened]
  );

  const onCloseImpl = useCallback(() => {
    setOpened(false);
  }, []);

  const onDelegateImpl = useCallback(() => {
    params.onDelegate?.();
    // Don't close the modal, allow user to see updated list
  }, [params]);

  return (
    <DelegateContext.Provider value={{ delegate }}>
      {children}
      <DelegateModal {...params} opened={opened} onClose={onCloseImpl} onDelegate={onDelegateImpl} />
    </DelegateContext.Provider>
  );
}

/**
 * Hook to use the delegate modal.
 */
export function useDelegate() {
  const context = useContext(DelegateContext);

  if (!context) {
    throw new Error('useDelegate must be used within a DelegateProvider');
  }

  return context;
}
